import { NgTemplateOutlet } from "@angular/common";
import { Component, computed, forwardRef, input, signal, viewChild } from "@angular/core";
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from "@angular/forms";
import { NgSelectComponent, NgSelectModule } from "@ng-select/ng-select";
import { CategorizedStatus, groupStatuses, statusSearchFn } from "./status-grouping";

/** Status-Form, die die Auswahl anzeigt (inkl. optionaler Flags für die Icons). */
export interface SelectableStatus extends CategorizedStatus {
  id?: number;
  closed?: boolean;
  canceled?: boolean;
  report_closed?: boolean;
}

/**
 * Gruppierte Statusauswahl (hawk#606) auf Basis von ng-select.
 *
 * Gruppen = Hauptkategorie (Überschrift = Label des 0er-Status bzw. "7.x"),
 * Legacy-Status in „Ohne Kategorie“ am Ende, Suche über das Label.
 * Wert = Status-ID; nutzbar mit `formControlName`, `[formControl]` und `ngModel`.
 */
@Component({
  selector: "hawk-status-select",
  standalone: true,
  imports: [FormsModule, NgSelectModule, NgTemplateOutlet],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => StatusSelectComponent),
      multi: true,
    },
  ],
  host: {
    class: "block",
  },
  template: `
    <ng-select
      class="w-full"
      [items]="options()"
      bindLabel="label"
      bindValue="id"
      groupBy="group"
      [selectableGroup]="false"
      [searchFn]="searchFn"
      [clearable]="clearable()"
      [loading]="loading()"
      [placeholder]="placeholder()"
      [appendTo]="appendTo()"
      [required]="required()"
      [disabled]="disabled()"
      notFoundText="Kein Status gefunden"
      loadingText="Status werden geladen …"
      [ngModel]="value()"
      (ngModelChange)="select($event)"
      (blur)="onTouched()"
    >
      <ng-template ng-optgroup-tmp let-item="item">
        <span class="block border-b border-base-300 pb-1 text-sm font-semibold text-base-content">{{ item.group }}</span>
      </ng-template>
      <ng-template ng-label-tmp let-item="item">
        <ng-container *ngTemplateOutlet="statusTpl; context: { $implicit: item }" />
      </ng-template>
      <ng-template ng-option-tmp let-item="item">
        <ng-container *ngTemplateOutlet="statusTpl; context: { $implicit: item }" />
      </ng-template>
    </ng-select>

    <ng-template #statusTpl let-item>
      <span class="inline-flex items-center gap-1">
        @if (item.closed) {
          <span class="fa fa-check-circle text-green-600" title="Geschlossen"></span>
        }
        @if (item.canceled) {
          <span class="fa fa-ban text-error" title="Storniert"></span>
        }
        @if (item.report_closed) {
          <span class="fa fa-hand-paper-o text-base-content/50" title="Bericht gesperrt"></span>
        }
        <span>{{ item.label }}</span>
      </span>
    </ng-template>
  `,
})
export class StatusSelectComponent implements ControlValueAccessor {
  /** Status des Projekts, idealerweise schon vom Backend sortiert. */
  public statuses = input<readonly SelectableStatus[] | null | undefined>([]);
  /** Optional: markiert einzelne Status als nicht wählbar (z. B. MT „abgelehnt“). */
  public isOptionDisabled = input<((status: SelectableStatus) => boolean) | undefined>(undefined);
  public loading = input(false);
  public clearable = input(false);
  public required = input(false);
  public placeholder = input("");
  /** z. B. "body", wenn das Dropdown in einem Modal sonst abgeschnitten würde */
  public appendTo = input<string | undefined>(undefined);

  protected readonly searchFn = statusSearchFn;
  protected options = computed(() => groupStatuses(this.statuses(), this.isOptionDisabled()));
  protected value = signal<number | null>(null);
  protected disabled = signal(false);
  private picker = viewChild(NgSelectComponent);

  private onChange: (value: number | null) => void = () => {};
  protected onTouched: () => void = () => {};

  /** Öffnet das Dropdown (z. B. direkt nach dem Start eines AG-Grid-Editors). */
  public open(): void {
    this.picker()?.open();
  }

  protected select(value: number | null): void {
    this.value.set(value ?? null);
    this.onChange(value ?? null);
  }

  public writeValue(value: number | string | null): void {
    // Formulare liefern die ID teils als String (alte <select>-Bindung)
    const numeric = value === null || value === undefined || value === "" ? null : Number(value);
    this.value.set(Number.isNaN(numeric) ? null : numeric);
  }

  public registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }
}
